/**
 * CoInvestmentGraph — radial SVG visualisation of an investor's
 * co-investor relationships.
 *
 * Center node = the focal investor. Orbital nodes = co-investors,
 * positioned around the center. Line thickness encodes deal count
 * (more deals together = thicker line). Node size encodes the
 * relative number of deals each co-investor has done with the focal.
 *
 * Pure SVG, no extra deps. Caps at 12 co-investors visible (rest get
 * a "+N more" hint) to keep it readable.
 */
import React from 'react';

interface CoInvestor {
  id: number;
  co_investor_id?: number | null;
  co_investor_name: string;
  deal_count?: number | null;
  sectors?: string[];
  last_co_investment_date?: string | null;
}

interface Props {
  focalInvestorName: string;
  coInvestments: CoInvestor[];
  size?: number;
  onNodeClick?: (coInvestorId: number) => void;
}

const MAX_VISIBLE = 12;

const CoInvestmentGraph: React.FC<Props> = ({
  focalInvestorName,
  coInvestments,
  size = 460,
  onNodeClick,
}) => {
  if (!coInvestments || coInvestments.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic py-8 text-center">
        No co-investment data yet.
      </div>
    );
  }

  // Sort by deal_count desc, take top N
  const sorted = [...coInvestments]
    .sort((a, b) => (b.deal_count || 0) - (a.deal_count || 0));
  const visible = sorted.slice(0, MAX_VISIBLE);
  const overflow = sorted.length - visible.length;

  const cx = size / 2;
  const cy = size / 2;
  const orbit = (size / 2) - 60;

  const maxDeals = Math.max(...visible.map((c) => c.deal_count || 1), 1);

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Edges first so they render under the nodes */}
        {visible.map((c, i) => {
          const angle = (i / visible.length) * 2 * Math.PI - Math.PI / 2;
          const x = cx + orbit * Math.cos(angle);
          const y = cy + orbit * Math.sin(angle);
          const strokeWidth = 1 + ((c.deal_count || 1) / maxDeals) * 4;
          return (
            <line
              key={`edge-${c.id}`}
              x1={cx}
              y1={cy}
              x2={x}
              y2={y}
              stroke="#cbd5e1"
              strokeWidth={strokeWidth}
              opacity={0.7}
            />
          );
        })}

        {/* Co-investor nodes */}
        {visible.map((c, i) => {
          const angle = (i / visible.length) * 2 * Math.PI - Math.PI / 2;
          const x = cx + orbit * Math.cos(angle);
          const y = cy + orbit * Math.sin(angle);
          const r = 14 + ((c.deal_count || 1) / maxDeals) * 8;
          const labelOffset = r + 6;
          // Place label outward from center
          const lx = cx + (orbit + labelOffset) * Math.cos(angle);
          const ly = cy + (orbit + labelOffset) * Math.sin(angle);
          const isClickable = !!(c.co_investor_id && onNodeClick);
          const anchor =
            Math.cos(angle) > 0.3 ? 'start' :
            Math.cos(angle) < -0.3 ? 'end' : 'middle';

          return (
            <g
              key={`node-${c.id}`}
              style={{ cursor: isClickable ? 'pointer' : 'default' }}
              onClick={() => isClickable && c.co_investor_id && onNodeClick?.(c.co_investor_id)}
            >
              <circle
                cx={x}
                cy={y}
                r={r}
                fill="#3b82f6"
                stroke="#1e40af"
                strokeWidth={1.5}
              />
              <text
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="central"
                fill="#fff"
                fontSize={10}
                fontWeight={600}
              >
                {c.deal_count ?? '—'}
              </text>
              <text
                x={lx}
                y={ly}
                textAnchor={anchor}
                dominantBaseline="central"
                fill="#1f2937"
                fontSize={11}
                fontWeight={500}
              >
                {c.co_investor_name.length > 24
                  ? c.co_investor_name.slice(0, 22) + '…'
                  : c.co_investor_name}
              </text>
            </g>
          );
        })}

        {/* Center (focal) node */}
        <circle
          cx={cx}
          cy={cy}
          r={32}
          fill="#1e40af"
          stroke="#1e3a8a"
          strokeWidth={2}
        />
        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#fff"
          fontSize={9}
          fontWeight={600}
        >
          YOU
        </text>
        <text
          x={cx}
          y={cy + 8}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#fff"
          fontSize={8}
        >
          {focalInvestorName.length > 14
            ? focalInvestorName.slice(0, 12) + '…'
            : focalInvestorName}
        </text>
      </svg>

      <div className="mt-2 text-xs text-gray-500 text-center">
        Node size + edge thickness = deals together. Click a node to navigate.
        {overflow > 0 && (
          <> · <span className="font-medium text-gray-700">+{overflow} more co-investors not shown</span></>
        )}
      </div>
    </div>
  );
};

export default CoInvestmentGraph;
